/**
 * The keyboard and mouse bindings, as shown under Settings → Key bindings.
 *
 * This is the reference card, not the mapper: `input/input.ts` owns what the keys do, and
 * `ui/raidbar.ts` owns the menu-strip caps. If a binding changes there, change it here. It
 * used to live as a paragraph on the start screen; Chris (2026-09-04) asked for it to move
 * into the HUD's Settings menu as a "Key bindings" entry instead.
 */

export interface KeyBind {
  /** Keycaps, in the order they should be drawn. */
  keys: readonly string[];
  /** What it does, in the game's voice, short. */
  does: string;
}

export interface KeyBindGroup {
  title: string;
  binds: readonly KeyBind[];
}

export const KEYBINDS: readonly KeyBindGroup[] = [
  {
    title: 'Move',
    binds: [
      { keys: ['W', 'S'], does: 'Forward and back' },
      { keys: ['A', 'D'], does: 'Turn · strafe while the right button is held' },
      { keys: ['SHIFT'], does: 'Sprint (drains stamina)' },
      { keys: ['SPACE'], does: 'Jump · mantle a ledge in front of you' },
      { keys: ['X'], does: 'Sit (toggle)' },
    ],
  },
  {
    title: 'Camera',
    binds: [
      { keys: ['RMB'], does: 'Hold to orbit — the camera and your body turn together' },
      { keys: ['LMB', 'RMB'], does: 'Both held: run forward' },
      { keys: ['WHEEL'], does: 'Zoom the camera in and out' },
    ],
  },
  {
    title: 'Fight',
    binds: [
      { keys: ['LMB'], does: 'Select a target · click empty ground to drop it' },
      { keys: ['TAB'], does: 'Target the nearest · again for the next one out' },
      { keys: ['SHIFT', 'TAB'], does: 'Cycle back in' },
      { keys: ['1', '2', '3', '4', '5', '6'], does: 'Abilities' },
    ],
  },
  {
    title: 'Loot',
    binds: [
      { keys: ['E', 'RMB'], does: 'Interact · speak · open a locker or bag · revive (hold E)' },
      { keys: ['B'], does: 'Bag (I also works)' },
      { keys: ['T'], does: 'Take everything in the open container' },
      { keys: ['RMB'], does: 'On a bag square: use, drop or read what is in it' },
    ],
  },
  {
    title: 'Panels',
    binds: [
      { keys: ['C'], does: 'Character — what you are wearing and what it is worth' },
      { keys: ['J'], does: 'Quests' },
      { keys: ['K'], does: 'Calling' },
      { keys: ['M'], does: 'Zone map' },
      { keys: ['O'], does: 'Scores' },
      { keys: ['ENTER'], does: 'Chat' },
      { keys: ['F8'], does: 'Feedback terminal' },
      { keys: ['L'], does: 'Settings' },
      { keys: ['ESC'], does: 'Drop the target, then close any panel' },
    ],
  },
];
